import { Empty, Tag, Tooltip, Typography } from 'antd';
import { addDays, addMonths, differenceInCalendarDays, format, parseISO, startOfDay, startOfMonth } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type {
  IncidentTaskStatus,
  ProcessTimeline,
  ProcessTimelineDependencyEvent,
  ProcessTimelineLane,
  ProcessTimelineScheduleTarget,
  UserRole,
} from '../types/incident';

const { Text } = Typography;

const laneHeight = 78;
const laneLabelWidth = 126;
const topPadding = 34;
const rightPadding = 32;
const chartWidth = 980;

const statusColors: Record<IncidentTaskStatus, string> = {
  OPEN: '#94a3b8',
  IN_PROGRESS: '#2563eb',
  ON_HOLD: '#f59e0b',
  RESOLVED: '#16a34a',
  CLOSED: '#475569',
};

const targetColors = {
  PENDING: '#64748b',
  ACHIEVED: '#16a34a',
  OVERDUE: '#dc2626',
};

const roleOrder: UserRole[] = ['ISSUING_PARTY', 'SERVICE', 'QUALITY'];

const toDisplayDate = (value: string) => parseISO(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T23:59:59` : value);
const toTime = (value: string | null | undefined) => (value ? toDisplayDate(value).getTime() : null);

const clampPercent = (value: number) => Math.max(0, Math.min(1, value));

interface Props {
  timeline: ProcessTimeline | null;
  onActivitySelect?: (activityId: string) => void;
}

export const ProcessTimelineChart = ({ timeline, onActivitySelect }: Props) => {
  const { t } = useTranslation();

  if (!timeline) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('incident.detail.processTimeline.empty')} />;
  }

  const start = toTime(timeline.summary.start_at) || Date.now();
  const end = Math.max(toTime(timeline.summary.end_at) || Date.now(), start + 24 * 60 * 60 * 1000);
  const plotWidth = chartWidth - laneLabelWidth - rightPadding;
  const chartHeight = topPadding + laneHeight * timeline.lanes.length + 22;
  const laneByRole = new Map(timeline.lanes.map((lane) => [lane.role, lane]));
  const closedTarget = timeline.lanes
    .flatMap((lane) => lane.schedule_targets)
    .find((target) => target.milestone_type === 'CLOSED' && target.achieved_at);
  const isClosedTimeline = Boolean(closedTarget);
  const scaleX = (value: string | null | undefined) => {
    const time = toTime(value);
    if (!time) return laneLabelWidth;
    return laneLabelWidth + clampPercent((time - start) / (end - start)) * plotWidth;
  };
  const terminalX = scaleX(closedTarget?.achieved_at || timeline.generated_at);
  const laneY = (role: UserRole) => topPadding + roleOrder.indexOf(role) * laneHeight;
  const monthTicks = [];
  for (let cursor = startOfMonth(new Date(start)); cursor.getTime() <= end; cursor = addMonths(cursor, 1)) {
    monthTicks.push(format(cursor, 'yyyy-MM'));
  }
  const dayTicks = [];
  for (let cursor = startOfDay(new Date(start)); cursor.getTime() <= end; cursor = addDays(cursor, 1)) {
    dayTicks.push(format(cursor, "yyyy-MM-dd'T'HH:mm:ss"));
  }

  const formatDelta = (delta: number | null | undefined) => {
    if (typeof delta !== 'number') return null;
    return delta > 0 ? `+${delta}` : `${delta}`;
  };

  const renderTargetLabel = (target: ProcessTimelineScheduleTarget) => {
    const adjusted = target.adjusted_due_at || target.original_due_at;
    const slaDelta = formatDelta(target.delta_days);
    const plannedDelta = formatDelta(target.planned_delta_days);
    const achievedDelta = formatDelta(target.achieved_delta_days);
    return [
      t(`incident.scheduleMilestone.${target.milestone_type}`),
      target.original_due_at ? `${t('incident.detail.processTimeline.initialDue')} ${format(toDisplayDate(target.original_due_at), 'M/d')}` : null,
      adjusted ? `${t('incident.detail.processTimeline.adjustedDue')} ${format(toDisplayDate(adjusted), 'M/d')}` : null,
      target.planned_due_at ? `${t('incident.detail.processTimeline.plannedDue')} ${format(toDisplayDate(target.planned_due_at), 'M/d')}` : null,
      target.achieved_at ? `${t('incident.detail.processTimeline.achieved')} ${format(toDisplayDate(target.achieved_at), 'M/d HH:mm')}` : null,
      slaDelta ? t('incident.detail.processTimeline.slaDelta', { days: slaDelta }) : null,
      plannedDelta ? t('incident.detail.processTimeline.plannedDelta', { days: plannedDelta }) : null,
      achievedDelta ? t('incident.detail.processTimeline.achievedDelta', { days: achievedDelta }) : null,
    ]
      .filter(Boolean)
      .join(' · ');
  };

  const renderPlannedWindowLabel = (target: ProcessTimelineScheduleTarget) =>
    [
      t('incident.detail.processTimeline.plannedWindow'),
      t(`incident.scheduleMilestone.${target.milestone_type}`),
      target.planned_due_at ? `${t('incident.detail.processTimeline.plannedDue')} ${format(toDisplayDate(target.planned_due_at), 'M/d')}` : null,
    ]
      .filter(Boolean)
      .join(' · ');

  const renderDependencyPath = (event: ProcessTimelineDependencyEvent) => {
    if (!event.to_role) return null;
    const x = scaleX(event.occurred_at);
    const y1 = laneY(event.from_role) + 36;
    const y2 = laneY(event.to_role) + 36;
    const direction = event.event_type === 'ON_HOLD_REQUESTED' ? 'timelineDependencyRequest' : 'timelineDependencyAnswer';
    const markerId = event.event_type === 'ON_HOLD_REQUESTED' ? 'timelineArrowRequest' : 'timelineArrowAnswer';
    return (
      <g key={event.id}>
        <path
          className={`timelineDependency ${direction}`}
          d={`M ${x} ${y1} L ${x} ${y2}`}
          markerEnd={`url(#${markerId})`}
          onClick={() => onActivitySelect?.(event.id)}
        />
        <circle
          className={`timelineDependencyStartDot ${direction}`}
          cx={x}
          cy={y1}
          r={2.5}
          onClick={() => onActivitySelect?.(event.id)}
        />
        <circle
          className={`timelineDependencyDot ${direction}`}
          cx={x}
          cy={y2}
          r={3}
          onClick={() => onActivitySelect?.(event.id)}
        />
      </g>
    );
  };

  const renderLane = (lane: ProcessTimelineLane) => {
    const y = laneY(lane.role);
    return (
      <g key={lane.role}>
        <text className="timelineLaneLabel" x={0} y={y + 40}>
          {t(`incident.role.${lane.role}`)}
        </text>
        <line className="timelineLaneGrid" x1={laneLabelWidth} x2={chartWidth - rightPadding} y1={y + 36} y2={y + 36} />
        {lane.actual_segments.map((segment, index) => {
          const x = scaleX(segment.start_at);
          const width = Math.max(8, scaleX(segment.end_at || timeline.summary.end_at) - x);
          return (
            <Tooltip
              key={`${segment.task_id}-${segment.status}-${segment.start_at}-${index}`}
              title={`${t(`incident.taskStatus.${segment.status}`)} · ${format(parseISO(segment.start_at), 'M/d HH:mm')}`}
            >
              <rect
                className="timelineActualSegment"
                fill={statusColors[segment.status]}
                height={16}
                rx={4}
                width={width}
                x={x}
                y={y + 28}
              />
            </Tooltip>
          );
        })}
        {lane.schedule_targets.map((target) => {
          const plannedDueTime = toTime(target.planned_due_at);
          const nowTime = toTime(timeline.generated_at);
          if (isClosedTimeline || !plannedDueTime || !nowTime || target.achieved_at || plannedDueTime <= nowTime) return null;
          const x = terminalX;
          const width = Math.max(8, scaleX(target.planned_due_at) - x);
          return (
            <Tooltip key={`${target.id}-planned-window`} title={renderPlannedWindowLabel(target)}>
              <rect className="timelinePlannedWindow" height={10} rx={3} width={width} x={x} y={y + 31} />
            </Tooltip>
          );
        })}
        {lane.schedule_targets.map((target) => {
          const initialX = target.original_due_at ? scaleX(target.original_due_at) : null;
          const adjustedX = target.adjusted_due_at ? scaleX(target.adjusted_due_at) : null;
          const plannedX = target.planned_due_at ? scaleX(target.planned_due_at) : null;
          const achievedX = target.achieved_at ? scaleX(target.achieved_at) : null;
          return (
            <Tooltip key={target.id} title={renderTargetLabel(target)}>
              <g className="timelineTarget" onClick={() => target.related_activity_id && onActivitySelect?.(target.related_activity_id)}>
                {initialX !== null && (
                  <line className="timelineInitialDueLine" x1={initialX} x2={initialX} y1={y + 10} y2={y + 34} />
                )}
                {adjustedX !== null && (
                  <>
                    <line className="timelineAdjustedDueLine" stroke={targetColors[target.status]} x1={adjustedX} x2={adjustedX} y1={y + 34} y2={y + 62} />
                    <circle fill={targetColors[target.status]} cx={adjustedX} cy={y + 35} r={4} />
                    <text className="timelineTargetLabel" x={adjustedX + 6} y={y + 39}>
                      {t(`incident.scheduleMilestoneShort.${target.milestone_type}`)}
                    </text>
                  </>
                )}
                {plannedX !== null && (
                  <polygon className="timelinePlannedMarker" points={`${plannedX},${y + 66} ${plannedX - 5},${y + 56} ${plannedX + 5},${y + 56}`} />
                )}
                {achievedX !== null && (
                  <rect className="timelineAchievedMarker" x={achievedX - 4} y={y + 24} width={8} height={8} transform={`rotate(45 ${achievedX} ${y + 28})`} />
                )}
              </g>
            </Tooltip>
          );
        })}
      </g>
    );
  };

  return (
    <div className="processTimeline">
      <div className="processTimelineSummary">
        <Text type="secondary">
          {t('incident.detail.processTimeline.range', {
            days: differenceInCalendarDays(parseISO(timeline.summary.end_at), parseISO(timeline.summary.start_at)) + 1,
          })}
        </Text>
        {timeline.summary.overdue_count > 0 && <Tag color="red">{t('incident.detail.processTimeline.overdue', { count: timeline.summary.overdue_count })}</Tag>}
        {isClosedTimeline && <Tag color="green">{t('incident.detail.processTimeline.closed')}</Tag>}
      </div>
      <div className="processTimelineScroller">
        <svg className="processTimelineSvg" width={chartWidth} height={chartHeight} role="img">
          <defs>
            <marker id="timelineArrowRequest" markerHeight={8} markerWidth={8} orient="auto" refX={7} refY={4}>
              <path className="timelineArrowHeadRequest" d="M 0 0 L 8 4 L 0 8 z" />
            </marker>
            <marker id="timelineArrowAnswer" markerHeight={8} markerWidth={8} orient="auto" refX={7} refY={4}>
              <path className="timelineArrowHeadAnswer" d="M 0 0 L 8 4 L 0 8 z" />
            </marker>
          </defs>
          {!isClosedTimeline && terminalX < chartWidth - rightPadding && (
            <rect className="timelineFutureArea" x={terminalX} y={topPadding - 18} width={chartWidth - rightPadding - terminalX} height={chartHeight - topPadding + 8} />
          )}
          {dayTicks.map((day) => {
            const x = scaleX(day);
            return <line key={day} className="timelineDayGrid" x1={x} x2={x} y1={topPadding - 8} y2={chartHeight - 10} />;
          })}
          {monthTicks.map((month) => {
            const x = scaleX(`${month}-01T00:00:00`);
            return (
              <g key={month}>
                <line className="timelineMonthGrid" x1={x} x2={x} y1={topPadding - 18} y2={chartHeight - 10} />
                <text className="timelineMonthLabel" x={x + 4} y={18}>
                  {format(parseISO(`${month}-01`), 'M月')}
                </text>
              </g>
            );
          })}
          <line className={isClosedTimeline ? 'timelineClosedLine' : 'timelineNowLine'} x1={terminalX} x2={terminalX} y1={topPadding - 18} y2={chartHeight - 10} />
          <text className={isClosedTimeline ? 'timelineClosedLabel' : 'timelineNowLabel'} x={terminalX + 4} y={chartHeight - 2}>
            {t(isClosedTimeline ? 'incident.detail.processTimeline.completed' : 'incident.detail.processTimeline.now')}
          </text>
          {roleOrder.map((role) => laneByRole.get(role)).filter(Boolean).map((lane) => renderLane(lane as ProcessTimelineLane))}
          {timeline.dependency_events.map(renderDependencyPath)}
        </svg>
      </div>
      <div className="processTimelineLegend">
        {(['OPEN', 'IN_PROGRESS', 'ON_HOLD', 'RESOLVED', 'CLOSED'] as IncidentTaskStatus[]).map((status) => (
          <span key={status} className="timelineLegendItem">
            <span className="timelineLegendSwatch" style={{ backgroundColor: statusColors[status] }} />
            {t(`incident.taskStatus.${status}`)}
          </span>
        ))}
        <span className="timelineLegendItem">
          <span className="timelineLegendLine timelineLegendInitial" />
          {t('incident.detail.processTimeline.initialDue')}
        </span>
        <span className="timelineLegendItem">
          <span className="timelineLegendLine timelineLegendAdjusted" />
          {t('incident.detail.processTimeline.adjustedDue')}
        </span>
        <span className="timelineLegendItem">
          <span className="timelineLegendTriangle" />
          {t('incident.detail.processTimeline.plannedDue')}
        </span>
        <span className="timelineLegendItem">
          <span className="timelineLegendPlannedWindow" />
          {t('incident.detail.processTimeline.plannedWindow')}
        </span>
        <span className="timelineLegendItem">
          <span className="timelineLegendNow" />
          {t('incident.detail.processTimeline.now')}
        </span>
      </div>
    </div>
  );
};
